// src/components/inputs/CustomerNameInput.jsx

import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    TextField,
    FormControl,
    Typography,
    Paper,
    useTheme,
    alpha,
    Autocomplete,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";

/**
 * Customer Name Input Component with Title Selection
 * Automatically formats name with proper title
 */
const CustomerNameInput = ({
    label,
    value = "",
    onChange,
    language = "en",
    required = false,
    disabled = false,
    error = false,
    helperText = "",
    fullWidth = true,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const isArabic = language === "ar";

    const parseValue = useCallback((val) => {
        if (!val) return { title: "NONE", name: "" };

        if (isArabic) {
            if (val.startsWith("أ ")) {
                return { title: "MR", name: val.substring(2).trim() };
            }
            if (val.startsWith("ة ")) {
                return { title: "MS", name: val.substring(2).trim() };
            }
        } else {
            const parts = val.split(" ");
            if (parts.length > 0) {
                const firstPart = parts[0].toUpperCase();
                if (firstPart === "MR" || firstPart === "MS") {
                    return {
                        title: firstPart,
                        name: parts.slice(1).join(" ")
                    };
                }
            }
        }

        return { title: "NONE", name: val };
    }, [isArabic]);

    const [title, setTitle] = useState("NONE");
    const [name, setName] = useState("");

    useEffect(() => {
        const parsed = parseValue(value);
        setTitle(parsed.title || "NONE");
        setName(parsed.name);
    }, [value, parseValue]);

    // تنسيق الاسم بشكل صحيح
    const formatName = (text) => {
        if (!text) return "";

        if (isArabic) {
            // للعربية: إرجاع الاسم كما هو
            return text.trim();
        } else {
            // للإنجليزية: Title Case
            return text
                .trim()
                .split(" ")
                .map(word => {
                    if (!word) return "";
                    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                })
                .join(" ");
        }
    };

    // بناء القيمة الكاملة
    const buildFullValue = (selectedTitle, customerName) => {
        const formattedName = formatName(customerName);
        if (!formattedName) return "";

        if (!selectedTitle || selectedTitle === "NONE") return formattedName;

        if (isArabic) {
            // للعربية
            const arabicTitle = selectedTitle === "MR" ? "أ" : "ة";
            return `${arabicTitle} ${formattedName}`;
        } else {
            // للإنجليزية
            const englishTitle = selectedTitle === "MR" ? "Mr" : "Ms";
            return `${englishTitle} ${formattedName}`;
        }
    };

    // const handleTitleChange = (e) => {
    //     const newTitle = e.target.value;
    //     setTitle(newTitle);

    //     const fullValue = buildFullValue(newTitle, name);
    //     onChange({ target: { value: fullValue } });
    // };

    const handleNameChange = (e) => {
        const newName = e.target.value;
        setName(newName);

        const fullValue = buildFullValue(title, newName);
        onChange({ target: { value: fullValue } });
    };

    const handleNameBlur = () => {
        // تنسيق الاسم عند فقدان التركيز
        const formattedName = formatName(name);
        if (formattedName !== name) {
            setName(formattedName);
            const fullValue = buildFullValue(title, formattedName);
            onChange({ target: { value: fullValue } });
        }
    };

    const titleOptions = [
        { value: "NONE", labelEn: "None", labelAr: "بدون لقب" },
        { value: "MR", labelEn: "Mr", labelAr: "السيد (أ)" },
        { value: "MS", labelEn: "Ms", labelAr: "السيدة (ة)" },
    ];

    return (
        <Box sx={{ width: fullWidth ? "100%" : "auto" }}>
            <Typography
                variant="caption"
                sx={{
                    display: "block",
                    mb: 0.5,
                    color: error ? "error.main" : "text.secondary",
                    fontWeight: 500,
                }}
            >
                {label} {required && <span style={{ color: theme.palette.error.main }}>*</span>}
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                {/* Title Selection */}
                <FormControl
                    size="small"
                    error={error}
                    disabled={disabled}
                    sx={{
                        minWidth: 100,
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: isDark
                                ? alpha(theme.palette.common.white, 0.05)
                                : alpha(theme.palette.common.black, 0.02),
                        },
                    }}
                >
                    <Autocomplete
                        id="title-select-autocomplete"
                        options={titleOptions}
                        getOptionLabel={(option) => isArabic ? option.labelAr : option.labelEn}
                        value={titleOptions.find(opt => opt.value === title) || titleOptions[0]}
                        onChange={(e, newValue) => {
                            const newTitle = newValue ? newValue.value : "NONE";
                            setTitle(newTitle);
                            const fullValue = buildFullValue(newTitle, name);
                            onChange({ target: { value: fullValue } });
                        }}
                        disabled={disabled}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={isArabic ? "اللقب" : "Title"}
                                size="small"
                                error={error}
                            />
                        )}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                padding: "3px", // adjust for Autocomplete small size
                            }
                        }}
                    />
                </FormControl>

                {/* Name Input */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder={
                        isArabic
                            ? "أدخل اسم العميل"
                            : "Enter customer name"
                    }
                    value={name}
                    onChange={handleNameChange}
                    onBlur={handleNameBlur}
                    disabled={disabled}
                    error={error}
                    InputProps={{
                        startAdornment: (
                            <PersonIcon
                                sx={{
                                    mr: 1,
                                    ml: isArabic ? 1 : 0,
                                    color: "action.active",
                                    fontSize: "1.2rem",
                                }}
                            />
                        ),
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: isDark
                                ? alpha(theme.palette.common.white, 0.05)
                                : alpha(theme.palette.common.black, 0.02),
                            "& input": {
                                height: "1.4375em",
                                padding: "8.5px 14px",
                            },
                        },
                    }}
                />
            </Box>

            {/* Preview or Helper Text */}
            {(value || helperText) && (
                <Box sx={{ mt: 0.5 }}>
                    {value && !error && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1,
                                mb: helperText ? 0.5 : 0,
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                borderRadius: 1,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "primary.main",
                                    fontWeight: 600,
                                    textAlign: isArabic ? "right" : "left",
                                }}
                            >
                                {isArabic ? "الاسم الكامل: " : "Full Name: "}
                                <span style={{ fontWeight: 700 }}>{value}</span>
                            </Typography>
                        </Paper>
                    )}
                    {helperText && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: error ? "error.main" : "text.secondary",
                                display: "block",
                            }}
                        >
                            {helperText}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default CustomerNameInput;
